/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { useCallback, useId, useMemo } from 'preact/compat';
import { Card, Button, Tag } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import CardLoader from '@/account/components/AccountLoaders';
import { AddressCardProps } from '@/account/types';
import { CustomerAddressesModel } from '@/account/data/models';
import '@/account/components/AddressCard/AddressCard.css';
import { Slot } from '@adobe-commerce/elsie/lib';
import { getAddressButtonAriaLabel } from '@/account/lib/getAddressButtonAriaLabel';

export const AddressCard: FunctionComponent<AddressCardProps> = ({
  slots,
  selectable,
  selectShipping,
  selectBilling,
  variant = 'secondary',
  minifiedView,
  keysSortOrder,
  addressData,
  loading,
  setAddressId,
  handleRenderModal,
  handleRenderForm,
}) => {
  const addressCardAriaId = useId();

  const minifiedViewKey = minifiedView ? 'minifiedView' : 'fullSizeView';

  const translations = useText({
    actionRemove: `Account.${minifiedViewKey}.Addresses.addressCard.actionRemove`,
    actionEdit: `Account.${minifiedViewKey}.Addresses.addressCard.actionEdit`,
    cardLabelShipping: `Account.${minifiedViewKey}.Addresses.addressCard.cardLabelShipping`,
    cardLabelBilling: `Account.${minifiedViewKey}.Addresses.addressCard.cardLabelBilling`,
    defaultLabelText: `Account.${minifiedViewKey}.Addresses.addressCard.defaultLabelText`,

    editAddressAriaLabelDefault: `Account.${minifiedViewKey}.Addresses.addressCard.ariaLabel.editButton.default`,
    editAddressAriaLabelShipping: `Account.${minifiedViewKey}.Addresses.addressCard.ariaLabel.editButton.shipping`,
    editAddressAriaLabelBilling: `Account.${minifiedViewKey}.Addresses.addressCard.ariaLabel.editButton.billing`,
    editAddressAriaLabelShippingAndBilling: `Account.${minifiedViewKey}.Addresses.addressCard.ariaLabel.editButton.shippingAndBilling`,

    removeAddressAriaLabelDefault: `Account.${minifiedViewKey}.Addresses.addressCard.ariaLabel.removeButton.default`,
    removeAddressAriaLabelShipping: `Account.${minifiedViewKey}.Addresses.addressCard.ariaLabel.removeButton.shipping`,
    removeAddressAriaLabelBilling: `Account.${minifiedViewKey}.Addresses.addressCard.ariaLabel.removeButton.billing`,
    removeAddressAriaLabelShippingAndBilling: `Account.${minifiedViewKey}.Addresses.addressCard.ariaLabel.removeButton.shippingAndBilling`,
  });

  const defaultBillingLabel = translations.cardLabelBilling.toLocaleUpperCase();
  const defaultShippingLabel =
    translations.cardLabelShipping.toLocaleUpperCase();
  const defaultLabelText = translations.defaultLabelText.toLocaleUpperCase();

  const getLabelStatus = useMemo(() => {
    const defaultValue = {
      shippingLabel: defaultShippingLabel,
      billingLabel: defaultBillingLabel,
      hideShipping: false,
      hideBilling: false,
    };

    if (!selectable) {
      return defaultValue;
    }

    if (selectShipping && !selectBilling) {
      return {
        shippingLabel: defaultLabelText,
        billingLabel: defaultLabelText,
        hideShipping: false,
        hideBilling: true,
      };
    } else if (selectBilling && !selectShipping) {
      return {
        shippingLabel: defaultLabelText,
        billingLabel: defaultLabelText,
        hideShipping: true,
        hideBilling: false,
      };
    } else if (selectBilling && selectShipping) {
      return defaultValue;
    }

    return defaultValue;
  }, [
    defaultBillingLabel,
    defaultLabelText,
    defaultShippingLabel,
    selectBilling,
    selectShipping,
    selectable,
  ]);

  const handleRemove = useCallback(() => {
    setAddressId?.(addressData?.id as string);
    handleRenderModal?.();
  }, [handleRenderModal, addressData?.id, setAddressId]);

  const handleEdit = useCallback(() => {
    setAddressId?.(addressData?.id as string);
    handleRenderForm?.();
  }, [handleRenderForm, addressData?.id, setAddressId]);

  const normalizeAddress = useMemo(() => {
    if (!keysSortOrder) return [];

    const { region, ...address } = addressData as CustomerAddressesModel;

    const modifyAddressData = {
      ...address,
      ...region,
    } as any;

    return keysSortOrder
      .filter(({ name }) => modifyAddressData[name as string])
      .map((item) => {
        return {
          name: item.name,
          orderNumber: item.orderNumber,
          value: modifyAddressData[item.name as string],
          label: item.label,
        };
      });
  }, [addressData, keysSortOrder]);

  const { shippingLabel, billingLabel, hideShipping, hideBilling } =
    getLabelStatus;

  return (
    <Card
      variant={variant}
      className="account-address-card"
      data-testid="addressCard"
    >
      {loading ? (
        <CardLoader />
      ) : (
        <>
          <div className="account-address-card__action">
            {handleRenderModal ? (
              <Button
                // @ts-ignore
                type={'button'}
                variant="tertiary"
                onClick={handleRemove}
                data-testid="removeButton"
                aria-label={getAddressButtonAriaLabel(
                  addressData?.defaultShipping,
                  addressData?.defaultBilling,
                  translations,
                  'removeButton'
                )}
                aria-describedby={addressCardAriaId}
              >
                {translations.actionRemove}
              </Button>
            ) : null}
            {handleRenderForm ? (
              <Button
                // @ts-ignore
                type={'button'}
                variant="tertiary"
                onClick={handleEdit}
                className="account-address-card__action--editbutton"
                data-testid="editButton"
                aria-label={getAddressButtonAriaLabel(
                  addressData?.defaultShipping,
                  addressData?.defaultBilling,
                  translations,
                  'editButton'
                )}
                aria-describedby={addressCardAriaId}
              >
                {translations.actionEdit}
              </Button>
            ) : null}
          </div>
          <div
            className="account-address-card__description"
            id={addressCardAriaId}
          >
            {slots?.AddressCard ? (
              <Slot
                name="AddressCard"
                slot={slots?.AddressCard}
                context={{ addressData: normalizeAddress }}
              />
            ) : (
              <>
                {normalizeAddress.map((el, index) => {
                  const value = !el.label
                    ? el?.value
                    : `${el.label}: ${el?.value}`;

                  return (
                    <p key={index} data-testid={`${el.name}_${index}`}>
                      {value}
                    </p>
                  );
                })}
              </>
            )}
          </div>
          {(addressData?.defaultShipping || addressData?.defaultBilling) &&
          !selectable ? (
            <div className="account-address-card__labels">
              {addressData?.defaultShipping ? (
                <Tag label={defaultShippingLabel} />
              ) : null}
              {addressData?.defaultBilling ? (
                <Tag label={defaultBillingLabel} />
              ) : null}
            </div>
          ) : null}
          {selectable ? (
            <div className="account-address-card__labels">
              {!hideShipping && addressData?.defaultShipping ? (
                <Tag label={shippingLabel} />
              ) : null}
              {!hideBilling && addressData?.defaultBilling ? (
                <Tag label={billingLabel} />
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
};
